import { Button, Col, List, Modal, Row } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dompurify from 'dompurify';
import gql from 'graphql-tag';
import _ from 'lodash';
import { PureComponent, createRef } from 'react';
import { withApollo } from 'react-apollo';
import ReactGA from 'react-ga';
import { Link } from 'react-router-dom';
import { queryBonusByContactIdIndex } from 'src/_shared/api/graphql/custom/bonuses/';
import { GetJobForMatch } from 'src/_shared/api/graphql/custom/jobMatch/';
import { GetUser } from 'src/_shared/api/graphql/custom/users/';
import Onboarding from 'src/_shared/components/onboarding/OnboardingComponent.jsx';
import { SearchBanner } from 'src/_shared/components/search-banner/index.js';
import Spinner from 'src/_shared/components/spinner/SpinnerComponent.jsx';
import { EMPLOYEE_DASHBOARD, USER_ROLES } from 'src/_shared/constants/index.js';
import { queryContactsByAtsIdIndex } from 'src/_shared/api/graphql/custom/contacts/contactsByAtsIdGraphql';
import {
	getSetErrorImageURL,
	getSize,
	getTextColorFromBGColor,
	ml,
	parse,
	searchOpenSearchNetwork,
} from 'src/_shared/services/utils.js';
import { COLORS } from 'src/_shared/styles/colors.js';
import MetabaseDashboardEmbed from 'src/dashboard/tiles/metabase/MetabaseDashboardEmbed';
import Logo from '../_shared/assets/erin_lightgray.png';
import OnDeckReferralModal from '../_shared/components/on-deck/general-referral-modal/SubmitGeneralReferralModalComponent.jsx';
import { AdminAnalyticsComponent } from './admin-analytics';
import {
	LinkStyle,
	NoSmartReferrals,
	NoSmartReferralsWithLogo,
	cardListStyle,
	linkText,
} from './dashboardStyles.js';
import {
	AnnouncementCard,
	ApplyInternally,
	EmpLeaderBoard,
	ExtendedTotalReferralCard,
	GeneralReferral,
	HotJobs,
	JobAlert,
	JobCard,
	JobForYouCard,
	JobMatchCard,
	JobNetworkCard,
	PointCard,
	RankingCard,
	RecentReferralCard,
	ReferralCard,
	ReferralPolicyCard,
	SendReferral,
} from './tiles';

const sanitizer = dompurify.sanitize;
dayjs.extend(customParseFormat);

class DashboardComponent extends PureComponent {
	constructor(props) {
		super(props);
		const jobMatches = props?.currentUser?.jobMatches || [];
		let allJobMatches = _.orderBy(jobMatches, ['relevance'], ['desc', 'asc'])
			.filter(
				(match) =>
					!match?.job?.hideImInterested &&
					props?.currentUser?.companyId === match?.job?.companyId
			)
			.map((match) => {
				const newMatch = _.cloneDeep(match);
				newMatch.job.location = parse(match?.job?.location);
				return newMatch;
			});

		if (props.currentUser.role !== USER_ROLES.EXTENDED_USER) {
			// Remove job matches that base users can't apply to
			allJobMatches = allJobMatches.filter(
				(match) =>
					!(
						(props?.currentUser?.company?.allowSelfReferralsInternalLink ||
							false) &&
						!(match?.job?.internalJobLink || null)
					)
			);
		}

		this.state = {
			allJobMatches,
			company: props?.currentUser?.company,
			customText: props?.company?.customText
				? JSON.parse(props.company.customText)
				: null,
			isLoading: true,
			companyData: props.companyData,
			dashboardSearchBG: props.company?.dashboardSearchBG,
			referralLeaderboard: false,
			pointsLeaderboard: true,
			isLoginPopupVisible: localStorage.getItem('showFirstTimePopup')
				? localStorage.getItem('showFirstTimePopup') !== 'false'
				: false,
			searchQuery: '',
			visible: false,
			errorImageURL: '',
			matchingAtsIdContacts: [],
		};
	}

	async componentDidMount() {
		const { location = {}, currentUser = {}, setCurrentUser } = this.props;
		let matchingAtsIdContacts = this.props?.matchingAtsIdContacts || [];
		const { bonuses = [] } = this.state;
		const allBonuses = [...bonuses];
		const host = window.location.hostname;

		if (host === 'referrals.aus.com') {
			ReactGA.initialize('UA-128630809-2');
			ReactGA.pageview(window.location.pathname + window.location.search);
		} else if (host === 'app.erinapp.com') {
			ReactGA.initialize('UA-128630809-3');
			ReactGA.pageview(window.location.pathname + window.location.search);
		}
		this.successMessage(location?.state?.successMessage);

		if (currentUser?.accountClaim?.atsId) {
			const { data: atsIdContacts } = await this.props.client.query({
				query: queryContactsByAtsIdIndex,
				variables: { atsId: currentUser?.accountClaim?.atsId },
			});
			if (atsIdContacts?.queryContactsByAtsIdIndex?.items) {
				matchingAtsIdContacts = atsIdContacts.queryContactsByAtsIdIndex.items;
			}
		}

		matchingAtsIdContacts.forEach(async (contact) => {
			const contactId = contact?.id;
			const { data } = await this.props.client.query({
				query: gql(queryBonusByContactIdIndex),
				variables: { contactId },
			});
			const allBonuses = data?.queryBonusByContactIdIndex?.items
				? [...data?.queryBonusByContactIdIndex?.items]
				: [];
			const candidateBonuses = allBonuses.filter(
				(bonus) => bonus?.recipientType === 'candidate'
			);
			allBonuses.push([...candidateBonuses]);
		});

		const { data } = await this.props.client.query({
			query: GetUser,
			variables: { id: this.props?.currentUser?.id },
			fetchPolicy: 'network-only',
		});

		const fetchedUser = data?.getUser || currentUser || {};
		if (currentUser) {
			const updatedUser = {
				...fetchedUser,
				displayAs: this.props.currentUser?.displayAs,
			};
			setCurrentUser(updatedUser);
		}

		await this.setQueryToState(this.state?.searchQuery);
		const errorImageURL =
			(await getSetErrorImageURL(currentUser?.company?.errorImage?.key)) ||
			null;

		this.setState(
			{
				bonuses: allBonuses,
				errorImageURL: errorImageURL,
				matchingAtsIdContacts: matchingAtsIdContacts,
			},
			async (prevState) => {
				if (prevState?.allJobMatches?.length <= 0) {
					await this.getTemporaryJobMatches();
				}
			}
		);
	}

	componentDidUpdate(prevProps) {
		const { bonuses = [] } = this.props;
		let matchingAtsIdContacts = this.state?.matchingAtsIdContacts || [];

		if (this.props.matchingAtsIdContacts !== this.state.matchingAtsIdContacts) {
			matchingAtsIdContacts.forEach(async (contact) => {
				const contactId = contact?.id;
				const { data } = await this.props.client.query({
					query: gql(queryBonusByContactIdIndex),
					variables: { contactId },
				});
				const allBonuses = data?.queryBonusByContactIdIndex?.items || [];
				const candidateBonuses = allBonuses.filter(
					(bonus) => bonus?.recipientType === 'candidate'
				);
				this.setState({ bonuses: [...bonuses, ...candidateBonuses] });
			});
		}
		if (prevProps.companyData !== this.props.companyData) {
			this.setState({
				companyData: this.props.companyData,
			});
		}

		if (prevProps?.company?.id !== this.props?.company?.id) {
			this.setState({
				customText: this.props.company?.customText
					? JSON.parse(this.props.company.customText)
					: null,
			});
		}
	}

	getTemporaryJobMatches = async () => {
		const parameters = {
			query: this.props?.currentUser?.title ?? '',
			size: 15,
			role: this.props?.currentUser?.role,
			currentUser: this.props?.currentUser?.id,
			filters: {
				companies: [this.props?.currentUser?.companyId],
				status: 'open',
			},
		};
		const response = await searchOpenSearchNetwork(parameters, 'erin-jobs');
		const temporaryMatches = response?.data || [];
		const top15 = temporaryMatches.slice(0, 15);
		const matches = [];
		await Promise.all(
			top15.map(async (item, index) => {
				const { data } = await this.props.client.query({
					query: GetJobForMatch,
					variables: { id: item?.jobId },
				});
				const job = data?.getJob || {};
				const match = {
					active: true,
					contactId: null,
					dateCreated: new Date().toISOString(),
					id: `${index}`,
					jobId: job?.id,
					job,
					matchStatus: null,
					relevance: item?.metaScore,
					userId: this.props?.currentUser?.id,
					temporary: true,
				};
				if (
					this.props.currentUser.role !== USER_ROLES.EXTENDED_USER &&
					(this.props.currentUser.company.allowSelfReferralsInternalLink ||
						false) &&
					!(job.internalJobLink || null)
				) {
					// do nothing
				} else {
					matches.push(match);
				}
			})
		);
		matches.push({
			id: 'update',
			active: true,
		});
		this.setState((_prevState) => ({
			allJobMatches: matches,
		}));
	};

	setQueryToState = async (searchQuery = '') => {
		const {
			filteredDepartments = [],
			filteredBonuses = [],
			filteredDistance = 0,
			filterOpenStatus = 'open',
			lat,
			lng,
			filteredSubCompanies = [],
			filteredJobLevels = [],
		} = this.state;
		const isExtendedUser = this.props?.currentUser?.role === 'extendedUser';
		const extendedCompaniesData =
			this.props?.currentUser?.extendedCompaniesData || [];
		const extendedCompanies = extendedCompaniesData.filter(
			(company) => company?.disableExtendedUserJobsView !== true
		);
		const comps =
			extendedCompanies && extendedCompanies.length > 0 && isExtendedUser
				? extendedCompanies
				: [this.props?.currentUser?.company];
		const companies = comps.map((company) => company?.id);
		const filteredCompanies =
			(this.state?.filterCompanies || []).length > 0
				? this.state.filterCompanies
				: companies;
		const unit =
			(this.props?.currentUser?.userGroup?.measurement || 'imperial') ===
			'metric'
				? 'km'
				: 'mi';
		clearTimeout(this.timer);
		this.setState({ searchQuery });
		this.timer = setTimeout(async () => {
			if (!this.state.isLoading) this.setState({ isLoading: true });
			const parameters = {
				query: this.state?.searchQuery || '',
				size: 300,
				coordinates: [{ lat, lng }],
				role: this.props?.currentUser?.role,
				currentUser: this.props?.currentUser?.id,
				filters: {
					bonuses: filteredBonuses,
					companies: filteredCompanies,
					departments: filteredDepartments,
					distance: filteredDistance,
					status: filterOpenStatus,
					subCompanies: filteredSubCompanies,
					jobLevels: filteredJobLevels,
				},
				unit,
			};

			const response = await searchOpenSearchNetwork(parameters, 'erin-jobs');

			const parametersHot = {
				query: this.state?.searchQuery || '',
				size: 300,
				coordinates: [{ lat, lng }],
				role: this.props?.currentUser?.role,
				currentUser: this.props?.currentUser?.id,
				filters: {
					companies: filteredCompanies,
					status: filterOpenStatus,
					isHotJob: true,
				},
				unit,
			};

			const responseHot = await searchOpenSearchNetwork(
				parametersHot,
				'erin-jobs'
			);

			if (response?.query === this.state?.searchQuery) {
				let searchedJobs = response?.data || [];
				const searchedHot = responseHot?.data || [];
				const filterOn =
					(filteredDistance.length > 0 && filteredDistance != '0') ||
					searchQuery.length > 0;

				searchedJobs = filterOn
					? searchedJobs || []
					: _.orderBy(
							searchedJobs || [],
							['isHotJob', 'dateCreated'],
							['desc', 'desc']
						);
				if (searchedJobs) {
					const filteredJobsGeneral = searchedJobs.filter(
						(item) => item.isGeneralReferral === true
					);
					const hotSearchedJobs =
						searchedHot.length > 0
							? searchedHot
							: searchedJobs.filter((item) => item.isHotJob === true);
					searchedJobs = searchedJobs.filter((item) => item.isHotJob !== true);
					this.setState({
						isLoading: false,
						searchedJobs,
						filteredJobsGeneral,
						hotSearchedJobs,
					});
				} else {
					this.setState({
						isLoading: false,
					});
				}
			}
		}, 700);
	};

	capitalizeIt = (string = '') => {
		return string.toUpperCase();
	};

	findBestMatch(array) {
		if (array) {
			if (array.length === 0) {
				return -1;
			}

			let maxIndex = -1;
			let maxRelevance = 9;
			for (const [i, m] of array.entries()) {
				if (m.relevance > maxRelevance && m.matchStatus !== false) {
					maxIndex = i;
					maxRelevance = m.relevance;
				}
			}

			return maxIndex;
		}

		return null;
	}

	formatDate(date, languageCode) {
		if (!date) return;
		const isoDate = new Date(date);
		const month = isoDate.getMonth() + 1;
		const day = isoDate.getDate();
		const year = isoDate.getFullYear();
		const hours = isoDate.getHours();
		const mins = ('0' + isoDate.getMinutes()).slice(-2);
		if (
			(date !== null && languageCode === 'FR') ||
			languageCode === 'DE' ||
			languageCode === 'ES' ||
			languageCode === 'RU' ||
			languageCode === 'European'
		) {
			return `${day}/${month}/${year} ${hours}${':'}${mins}`;
		}

		return `${month}/${day}/${year} ${hours}${':'}${mins}`;
	}

	handleBrowseJobs = (id) => {
		if (id) {
			this.props.history.push(`/browsejobs/${id}`);
		} else {
			this.props.history.push('/browsejobs');
		}
	};

	handleCancel = () => {
		this.setState({ visible: false });
	};

	handleCareerProfile = () => {
		this.props.history.push('/career');
	};

	handleEmployees = () => {
		this.props.history.push('/employees');
	};

	handleFirstLoginPopup = () => {
		localStorage.setItem('showFirstTimePopup', 'false');
		this.setState({ isLoginPopupVisible: false });
	};

	handleResize = () => {
		const getWindowDimensions = () => {
			const { innerWidth: width, innerHeight: height } = window;
			return {
				width,
				height,
			};
		};

		const windowDimensions = getWindowDimensions();
		this.setState({ windowDimensions });
	};

	notInterested = (match) => {
		const input = {
			id: match?.id,
			companyId: match?.job?.companyId,
			active: false,
		};
		if (!match?.temporary) this.props.onUpdateJobMatch(input);
		let allJobMatches = this.state?.allJobMatches || [];
		const index = allJobMatches.findIndex((m) => m.id === match.id);
		const updatedMatch =
			index >= 0 ? { ...allJobMatches[index], ...input } : null;

		if (updatedMatch) {
			allJobMatches[index] = updatedMatch;
		}

		const temporaryMatches = [];
		if (match?.temporary) {
			allJobMatches = allJobMatches.filter(
				(match) => match.id !== 'update' && match.active !== false
			);
			allJobMatches.map((match, index) => {
				temporaryMatches.push(match);
				if ((index + 1) % 3 === 0 || index === allJobMatches.length - 1)
					temporaryMatches.push({
						id: 'update',
						active: true,
					});
			});
		}

		const currentUser = this.props?.currentUser;
		currentUser.jobMatches = allJobMatches;
		if (match?.temporary) {
			this.setState({ allJobMatches: temporaryMatches });
		} else {
			this.props.setCurrentUser(currentUser);
		}
	};

	successMessage = (content = null) => {
		if (content === null) return;
		Modal.success({
			content,
		});
	};

	renderAdminAnalytics = () => {
		const { currentUser } = this.props;
		if (
			currentUser.role === USER_ROLES.ADMIN &&
			currentUser.displayAs === USER_ROLES.ADMIN
		) {
			return (
				<div>
					<AdminAnalyticsComponent
						companyId={currentUser.companyId}
						analyticsCardInputValues={
							this.props?.companyData?.analyticsCardInputValues
						}
						onUpdateCompany={this.props.onUpdateCompany}
					/>
				</div>
			);
		}

		return null;
	};

	render() {
		const ref = createRef();
		const {
			currentUser,
			departments,
			jobs = [],
			jobMatches,
			extendedReferrals = [],
			allMultiLingualData,
			announcements,
			userStats = {},
			leaderboards = {},
			userStatsLoading,
		} = this.props;
		const {
			company,
			companyData,
			customText,
			referralBonuses,
			isLoginPopupVisible,
			hotSearchedJobs,
			errorImageURL,
		} = this.state;

		const customGeneralModal = this.props?.companyData?.customGeneralModal
			? JSON.parse(this.props.companyData.customGeneralModal)
			: {};
		const generalCardHeader =
			customText?.generalCardHeader ??
			ml('SUBMIT', currentUser, allMultiLingualData);
		const generalCardSubheader =
			customText?.generalCardSubheader ??
			ml('A GENERAL REFERRAL', currentUser, allMultiLingualData);
		const generalCardButton =
			customText?.generalCardButton ??
			ml('SUBMIT REFERRAL', currentUser, allMultiLingualData);
		const referralCardHeader =
			customText?.referralCardHeader ??
			ml('REFER', currentUser, allMultiLingualData);
		const referralCardSubheader =
			customText?.referralCardSubheader ??
			ml('YOUR FRIENDS', currentUser, allMultiLingualData);
		const referralCardButton =
			customText?.referralCardButton ??
			ml('SEND A REFERRAL', currentUser, allMultiLingualData);
		const internalCardHeader =
			customText?.internalCardHeader ??
			ml('APPLY', currentUser, allMultiLingualData);
		const internalCardSubheader =
			customText?.internalCardSubheader ??
			ml('& GROW YOUR CAREER', currentUser, allMultiLingualData);
		const internalCardButton =
			customText?.internalCardButton ??
			ml('APPLY INTERNALLY', currentUser, allMultiLingualData);
		const leadCardHeader =
			customText?.leadCardHeader ??
			ml('SUBMIT', currentUser, allMultiLingualData);
		const leadCardSubheader =
			customText?.leadCardSubheader ??
			ml('A REFERRAL LEAD', currentUser, allMultiLingualData);
		const leadCardButton =
			customText?.leadCardButton ??
			ml('SUBMIT REFERRAL', currentUser, allMultiLingualData);
		const onboarding = parse(this.props?.company?.onboarding);
		const onboardingStatus = onboarding?.status;
		const announcement = announcements;
		const customStages = this.props?.currentUser?.company?.stages;
		const sendReferralColor =
			currentUser?.company?.sendReferralColor || '#E75DA7';
		const applyInternallyColor =
			currentUser?.company?.applyInternallyColor || '#14D6D3';
		const generalReferralColor =
			currentUser?.company?.generalReferralColor || '#7942E7';
		const referralLeadColor =
			currentUser?.company?.referralLeadColor || '#F7A34D';
		const sendReferralTextColor = getTextColorFromBGColor({
			backgroundColor: sendReferralColor,
		});
		const applyInternallyTextColor = getTextColorFromBGColor({
			backgroundColor: applyInternallyColor,
		});
		const generalReferralTextColor = getTextColorFromBGColor({
			backgroundColor: generalReferralColor,
		});
		const referralLeadTextColor = getTextColorFromBGColor({
			backgroundColor: referralLeadColor,
		});
		const stages = [];
		if (customStages) {
			const parsedData = parse(customStages);
			if (parsedData.length > 0) {
				stages.push(...parsedData);
			}
		}

		const publicLeaderboard = currentUser?.company?.publicLeaderboard || false;
		const hideReferralsRanking =
			currentUser?.company?.hideReferralsRanking || false;
		const whiteLabel = company?.whiteLabel;
		const recommendedReferral = this.findBestMatch(jobMatches);
		let recommendedJob;
		let matchesRecommendedJob;
		if (recommendedReferral && recommendedReferral >= 0) {
			recommendedJob = jobs.filter(
				(job) => job.id === jobMatches[recommendedReferral].jobId
			);
			if (recommendedJob.length > 0) {
				matchesRecommendedJob = jobs.filter(
					(job) => job.id === recommendedJob[0].jobId
				);
				matchNumber = matchesRecommendedJob.length + 1;
			}
		}
		const theme = this.props?.currentUser?.company?.theme;
		const careerProfile = parse(this.props?.currentUser?.careerProfile);
		const hasUploadedResume = Boolean(
			careerProfile && careerProfile?.resumeFile?.name
		);
		const allJobMatches = (this.state?.allJobMatches || []).filter(
			(match) => match?.active === true
		);
		let isPointsInfoVisible = false;
		const pointsSettings = this.state?.companyData?.pointsSettings;

		if (pointsSettings !== null && pointsSettings !== undefined) {
			const pointsSettingsData = parse(pointsSettings);
			isPointsInfoVisible = Boolean(pointsSettingsData.enabled);
		}

		if (!allMultiLingualData || allMultiLingualData.length === 0) {
			return null;
		}

		const popupTitle = this.props?.currentUser?.company?.popupTitle || '';
		const popupTitleContent =
			this.props?.currentUser?.company?.popupTitleContent || '';
		let employeeDashboard = currentUser?.company?.employeeDashboard || [];
		employeeDashboard = employeeDashboard ? parse(employeeDashboard) : [];
		let showReferSomeone = true;
		let showInternal = true;
		let showYourReferrals = true;
		let showRanking = true;
		let showPoints = true;
		let showLeaderboards = true;
		let showJobs = true;
		let showJobAlerts = true;
		let showAnnouncements = true;
		let showPolicy = true;
		let showJobsForYou = true;
		let showHotJobs = false;
		let showGeneralReferrals = false;
		let showReferralLead = false;
		let referOrder;
		let internalOrder;
		let yourReferralsOrder;
		let rankingOrder;
		let pointsOrder;
		let jobAlertsOrder;
		let leaderboardOrder;
		let announcementOrder;
		let policyOrder;
		let jobsForYouOrder;
		let hotJobsOrder;
		let jobsOrder;
		let generalReferralsOrder;
		let referralLeadOrder;
		if (employeeDashboard && employeeDashboard.length >= 0) {
			const referSomeoneData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.REFER_SOMEONE
			);
			showReferSomeone = Boolean(referSomeoneData);
			referOrder = referSomeoneData?.order;
			const internalMobilityData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.INTERNAL_MOBILITY
			);
			showInternal = Boolean(internalMobilityData);
			internalOrder = internalMobilityData?.order;
			const yourReferralsData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.YOUR_REFERRALS
			);
			showYourReferrals = Boolean(yourReferralsData);
			yourReferralsOrder = yourReferralsData?.order;
			const referralsMadeRankingData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.REFERRALS_MADE_RANKING
			);
			showRanking = Boolean(referralsMadeRankingData);
			rankingOrder = referralsMadeRankingData?.order;
			const pointsData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.POINTS
			);
			showPoints = Boolean(pointsData);
			pointsOrder = pointsData?.order;
			const jobsOverviewData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.JOBS_OVERVIEW
			);
			showJobs = Boolean(jobsOverviewData);
			jobsOrder = jobsOverviewData?.order;
			const leaderboardsData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.LEADERBOARDS
			);
			showLeaderboards = Boolean(leaderboardsData);
			leaderboardOrder = leaderboardsData?.order;
			const announcementsData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.ANNOUNCEMENTS
			);
			showAnnouncements = Boolean(announcementsData);
			announcementOrder = announcementsData?.order;
			const policyData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.POLICY
			);
			showPolicy = Boolean(policyData);
			policyOrder = policyData?.order;
			const jobsForYouData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.JOBS_FOR_YOU
			);
			showJobsForYou = Boolean(jobsForYouData);
			jobsForYouOrder = jobsForYouData?.order;

			const hotJobsData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.HOT_JOBS
			);
			showHotJobs = Boolean(hotJobsData);
			hotJobsOrder = hotJobsData?.order;

			const generalReferralsData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.GENERAL_REFERRAL
			);
			showGeneralReferrals = Boolean(generalReferralsData);
			generalReferralsOrder = generalReferralsData?.order;

			const referralLeadData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.REFERRAL_LEAD
			);
			showReferralLead = Boolean(referralLeadData);
			referralLeadOrder = referralLeadData?.order;

			const jobAlertsData = employeeDashboard.find(
				(x) => x.name === EMPLOYEE_DASHBOARD.JOB_ALERTS
			);
			showJobAlerts = Boolean(jobAlertsData);
			jobAlertsOrder = jobAlertsData?.order;
		}

		const pageSize = getSize(this.state.windowDimensions);

		return !currentUser?.company ? (
			<Spinner />
		) : (
			<main ref={ref} id="dashboard">
				{(currentUser.role === USER_ROLES.EMPLOYEE ||
					currentUser.role === USER_ROLES.MANAGER ||
					currentUser.displayAs === USER_ROLES.EMPLOYEE) &&
				this.props.company?.enableDashboardSearch ? null : (
					<div className="page-title">
						<h2 className="page-heading">
							{ml(`Hello`, currentUser, allMultiLingualData)},{' '}
							{currentUser?.firstName || ''}
						</h2>
					</div>
				)}

				{/* extended user dashboard */}
				{currentUser.displayAs === USER_ROLES.EXTENDED_USER && (
					<div>
						<Row>
							<Col xs={24} md={8}>
								<RecentReferralCard referrals={extendedReferrals} />
							</Col>
							<Col xs={24} md={8}>
								<ExtendedTotalReferralCard
									totalReferralsCount={extendedReferrals.length}
								/>
							</Col>
							{!currentUser.company?.disableExtendedUserJobsView && (
								<Col xs={24} md={8}>
									<JobNetworkCard
										extendedCompanies={
											this.props.currentUser.extendedCompaniesData
										}
									/>
								</Col>
							)}
						</Row>
						{!currentUser.company?.disableExtendedUserJobsView &&
							allJobMatches.length > 0 && (
								<>
									<div>
										<h2
											style={{ display: 'inline', marginBottom: 0 }}
											className="page-heading"
										>
											{ml(`New Job Matches`, currentUser, allMultiLingualData)}
										</h2>
										<Link
											style={{ display: 'inline', marginLeft: '6px' }}
											to="/browsejobs"
											className={linkText}
										>
											View All Jobs
										</Link>
									</div>
									<Row style={{ marginBottom: 20 }}>
										{allJobMatches.length > 0 ? (
											<List
												dataSource={allJobMatches}
												grid={{
													gutter: 10,
													xs: 1,
													sm: 1,
													md: 2,
													lg: 2,
													xl: 3,
													xxl: 4,
												}}
												className={cardListStyle}
												pagination={{
													responsive: true,
													pageSize,
													showSizeChanger: false,
												}}
												renderItem={(jobMatch) => (
													<List.Item>
														<Col key={jobMatch?.id}>
															<JobMatchCard
																handleCareerProfile={this.handleCareerProfile}
																allMultiLingualData={allMultiLingualData}
																currentUser={this.props.currentUser}
																jobMatch={jobMatch}
																notInterested={this.notInterested}
																theme={theme}
															/>
														</Col>
													</List.Item>
												)}
											/>
										) : (
											<div>
												{whiteLabel ? (
													<p className={NoSmartReferrals}>
														<img
															src={errorImageURL}
															alt="error image"
															width="150px"
														/>
													</p>
												) : (
													<p className={NoSmartReferrals}>
														<img alt="erin-logo" width={150} src={Logo} />
													</p>
												)}
												{hasUploadedResume ? (
													<p className={NoSmartReferralsWithLogo}>
														{ml(
															`We don’t have any recommended positions at this time.`,
															currentUser,
															allMultiLingualData
														)}
														<br />
														{ml(
															'Check back soon!',
															currentUser,
															allMultiLingualData
														)}
													</p>
												) : (
													<p className={NoSmartReferralsWithLogo}>
														<Link className={LinkStyle} to="/career">
															{ml(
																'Import your resume',
																currentUser,
																allMultiLingualData
															)}{' '}
														</Link>
														{ml(
															`to be automatically matched with open positions.`,
															currentUser,
															allMultiLingualData
														)}
														<br />
													</p>
												)}
											</div>
										)}
									</Row>
								</>
							)}
					</div>
				)}

				{(currentUser.displayAs === USER_ROLES.EMPLOYEE ||
					currentUser.role === USER_ROLES.MANAGER) && (
					<>
						{this.props.company?.enableDashboardSearch && (
							<SearchBanner
								imageS3Data={this.props?.company?.dashboardSearchBG}
								helloText={ml(`Hello`, currentUser, allMultiLingualData)}
								firstName={currentUser?.firstName}
								companyId={this.props?.company?.id}
								callToAction={
									<p>
										{ml('Find jobs at', currentUser, allMultiLingualData)}{' '}
										<strong style={{ display: 'inline-block' }}>
											{currentUser?.company?.name}
										</strong>
									</p>
								}
								placeholder={ml(
									'Search by location, job name and/or keywords',
									currentUser,
									allMultiLingualData
								)}
								style={{}}
								{...this.props}
							/>
						)}

						<div className="row">
							{showJobAlerts && (
								<div className="col-md-4" style={{ order: jobAlertsOrder }}>
									<JobAlert />
								</div>
							)}
							{showReferSomeone && (
								<div className="col-md-4" style={{ order: referOrder }}>
									<SendReferral
										{...this.props}
										allMultiLingualData={allMultiLingualData}
										currentUser={currentUser}
										referralCardHeader={referralCardHeader}
										referralCardSubheader={referralCardSubheader}
										referralCardButton={referralCardButton}
										background={sendReferralColor}
										textColor={sendReferralTextColor}
									/>
								</div>
							)}

							{showInternal && (
								<div className="col-md-4" style={{ order: internalOrder }}>
									<ApplyInternally
										{...this.props}
										allMultiLingualData={allMultiLingualData}
										currentUser={currentUser}
										internalCardHeader={internalCardHeader}
										internalCardSubheader={internalCardSubheader}
										internalCardButton={internalCardButton}
										background={applyInternallyColor}
										textColor={applyInternallyTextColor}
									/>
								</div>
							)}

							{showYourReferrals && (
								<div className="col-md-4" style={{ order: yourReferralsOrder }}>
									<ReferralCard
										title={ml(
											'Your Referrals',
											currentUser,
											allMultiLingualData
										)}
										currentUser={currentUser}
										allMultiLingualData={allMultiLingualData}
										height="height-equal"
										referralTotal={userStats.referralTotal}
										bonusTotal={userStats.bonusTotal}
										currency={userStats.currency}
									/>
								</div>
							)}

							{showRanking && !hideReferralsRanking && (
								<div className="col-md-4" style={{ order: rankingOrder }}>
									<div className="d-card height-equal">
										<div className="d-card-head">
											<h4 className="d-title">
												{this.capitalizeIt(
													ml(
														'Referral Ranking',
														currentUser,
														allMultiLingualData
													)
												)}
											</h4>
										</div>
										<RankingCard
											{...this.props}
											ranking={userStats.ranking}
											previousRanking={userStats.previousRanking}
										/>
									</div>
								</div>
							)}

							{isPointsInfoVisible && showPoints && (
								<div className="col-md-4" style={{ order: pointsOrder }}>
									<PointCard
										{...this.props}
										allMultiLingualData={allMultiLingualData}
										currentUser={currentUser}
										title={ml('Points', currentUser, allMultiLingualData)}
										WithReferralCount={this.state.WithReferralCount}
										NoReferralCount={this.state.NoReferralCount}
										JobTiers={
											this.state.TierWiseInfo ? this.state.TierWiseInfo : []
										}
										companyData={companyData}
										totalPoints={userStats.referralPointsTotal}
										userPointsRanking={userStats.pointsRanking}
										onClick={() => {
											this.handleBrowseJobs();
										}}
									/>
								</div>
							)}

							{showAnnouncements && (
								<div className="col-md-4" style={{ order: announcementOrder }}>
									<AnnouncementCard
										title={ml(
											'ANNOUNCEMENTS',
											currentUser,
											allMultiLingualData
										)}
										{...this.props}
										currentUser={currentUser}
										referralBonuses={referralBonuses}
										allMultiLingualData={allMultiLingualData}
										announcement={announcement}
										onClick={() => {
											this.handleEmployees();
										}}
									/>
								</div>
							)}

							{showJobs && (
								<div className="col-md-4" style={{ order: jobsOrder }}>
									<JobCard
										title={ml('Jobs', currentUser, allMultiLingualData)}
										currentUser={currentUser}
										allMultiLingualData={allMultiLingualData}
										openPositions={userStats.openJobTotal}
										onClick={() => {
											this.handleBrowseJobs();
										}}
									/>
								</div>
							)}

							{showLeaderboards && publicLeaderboard && (
								<div className="col-md-4" style={{ order: leaderboardOrder }}>
									<EmpLeaderBoard
										height="height-equal"
										title={ml('Leaderboards', currentUser, allMultiLingualData)}
										referralTotalLeaderboard={leaderboards.referralTotal}
										referralPointsLeaderboard={leaderboards.referralPoints}
										isLoading={userStatsLoading}
										allMultiLingualData={allMultiLingualData}
										currentUser={currentUser}
									/>
								</div>
							)}

							{showJobsForYou && allJobMatches && allJobMatches.length > 0 && (
								<div className="col-md-4" style={{ order: jobsForYouOrder }}>
									<JobForYouCard
										handleCareerProfile={this.handleCareerProfile}
										allMultiLingualData={allMultiLingualData}
										currentUser={this.props.currentUser}
										allJobMatches={allJobMatches}
										notInterested={this.notInterested}
										allowSelfReferrals={
											this.props?.currentUser?.company?.allowSelfReferrals
										}
										allowSelfReferralsInternalLink={
											this.props?.currentUser?.company
												?.allowSelfReferralsInternalLink
										}
										theme={theme}
										height="height-equal"
										onCreateReferral={this.props.onCreateReferral}
									/>
								</div>
							)}

							{showGeneralReferrals && (
								<div
									className="col-md-4"
									style={{ order: generalReferralsOrder }}
								>
									<div
										className="e-card-wrap"
										style={{ backgroundColor: generalReferralColor }}
									>
										<div
											className="e-card-title"
											style={{ color: generalReferralTextColor }}
										>
											{generalCardHeader}
										</div>
										<div
											className="e-card-subtitle"
											style={{ color: generalReferralTextColor }}
										>
											{generalCardSubheader}
										</div>
										<GeneralReferral
											{...this.props}
											allMultiLingualData={allMultiLingualData}
											generalCardButton={generalCardButton}
											currentUser={currentUser}
											customGeneralModal={customGeneralModal}
											companyData={companyData}
										/>
									</div>
								</div>
							)}

							{showReferralLead && (
								<div className="col-md-4" style={{ order: referralLeadOrder }}>
									<div
										className="e-card-wrap"
										style={{ backgroundColor: referralLeadColor }}
									>
										<div
											className="e-card-title"
											style={{ color: referralLeadTextColor }}
										>
											{leadCardHeader}
										</div>
										<div
											className="e-card-subtitle"
											style={{ color: referralLeadTextColor }}
										>
											{leadCardSubheader}
										</div>
										<Button
											type="button"
											className="c-btn-white"
											onClick={() =>
												this.setState({
													visible: true,
												})
											}
										>
											{leadCardButton}
										</Button>
									</div>
								</div>
							)}
							{showHotJobs && (
								<div className="col-md-4" style={{ order: hotJobsOrder }}>
									<HotJobs
										hotSearchedJobs={hotSearchedJobs}
										isLoading={this.state.isLoading}
										currentUser={currentUser}
										allMultiLingualData={allMultiLingualData}
									/>
								</div>
							)}
						</div>
					</>
				)}

				{(currentUser.displayAs === USER_ROLES.EMPLOYEE ||
					currentUser.role === USER_ROLES.MANAGER) && (
					<div style={{ order: policyOrder }}>
						{showPolicy && (
							<div className="row">
								<div className="col-12">
									{this.props?.currentUser?.company
										?.dashboardReferralPolicyText &&
									this.props?.currentUser?.company
										?.dashboardReferralPolicyText !== '<p><br></p>' ? (
										<ReferralPolicyCard {...this.props} minHeight="auto" />
									) : null}
								</div>
							</div>
						)}
					</div>
				)}
				{/* user dashboard */}
				{this.renderAdminAnalytics()}

				{currentUser.displayAs === USER_ROLES.ADMIN && (
					<MetabaseDashboardEmbed companyId={currentUser.company.id} />
				)}

				{(currentUser.displayAs === USER_ROLES.ADMIN ||
					currentUser.role === USER_ROLES.MANAGER) &&
					(!publicLeaderboard || !showLeaderboards) && (
						<Row>
							<Col xs={24} md={24}>
								{this.props?.currentUser?.company
									?.dashboardReferralPolicyText &&
								this.props?.currentUser?.company
									?.dashboardReferralPolicyText !== '<p><br></p>' ? (
									<ReferralPolicyCard {...this.props} minHeight="auto" />
								) : null}
							</Col>
						</Row>
					)}
				{onboardingStatus !== 'complete' &&
					this.props?.company?.accountType === 'partner' && (
						<Modal
							destroyOnClose
							closable={false}
							open={this.state.onboardingStatus !== 'complete'}
							width="1000px"
							footer={null}
							style={{
								backgroundColor: COLORS.white,
								borderRadius: 20,
								border: 'none',
								overflow: 'auto',
								padding: 0,
							}}
							bodyStyle={{ padding: 0, border: 'none' }}
							onCancel={this.handleTutorialPopup}
						>
							<Onboarding
								currentUser={currentUser}
								status={this.state?.onboardingStatus}
								{...this.props}
							/>
						</Modal>
					)}

				<OnDeckReferralModal
					currentUser={this.props.currentUser}
					visible={this.state.visible}
					handleCancel={this.handleCancel}
					jobData={this.props.job}
					referSomeoneText={ml(
						'Refer Someone',
						currentUser,
						allMultiLingualData
					)}
					doYouKnowText={ml(
						'Do you know someone who would thrive at',
						currentUser,
						allMultiLingualData
					)}
					submitThemText={ml(
						'Submit them to be considered for new opportunities.',
						currentUser,
						allMultiLingualData
					)}
					enterReferralText={ml(
						"Enter a referral and we'll send them information to apply.",
						currentUser,
						allMultiLingualData
					)}
					enterReferralInformationText={ml(
						'Enter Candidate Information',
						currentUser,
						allMultiLingualData
					)}
					firstNameText={ml('First Name', currentUser, allMultiLingualData)}
					lastNameText={ml('Last Name', currentUser, allMultiLingualData)}
					emailText={ml('Email Address', currentUser, allMultiLingualData)}
					textPlaceHolderText={ml('Text', currentUser, allMultiLingualData)}
					orText={ml('or', currentUser, allMultiLingualData)}
					clickHereText={ml('click here', currentUser, allMultiLingualData)}
					toAddExistingContactText={ml(
						'to add an existing contact',
						currentUser,
						allMultiLingualData
					)}
					includeMessageToContactText={ml(
						'Include a message to your contact',
						currentUser,
						allMultiLingualData
					)}
					optionalText={`(${ml('optional', currentUser, allMultiLingualData)})`}
					requiredText={`(${ml('required', currentUser, allMultiLingualData)})`}
					personalizeMessageText={ml(
						'Personalize the message to your referral',
						currentUser,
						allMultiLingualData
					)}
					messageHiringContactText={ml(
						'Message the Hiring Contact',
						currentUser,
						allMultiLingualData
					)}
					howKnowThemText={ml(
						'How do you know them, why are they a good fit, etc.',
						currentUser,
						allMultiLingualData
					)}
					clickHereResumeText={ml(
						'Click here',
						currentUser,
						allMultiLingualData
					)}
					attachResumeText={ml(
						'to attach a resume',
						currentUser,
						allMultiLingualData
					)}
					submitReferralText={ml(
						'Submit Referral',
						currentUser,
						allMultiLingualData
					)}
					allMultiLingualData={this.props?.allMultiLingualData}
					onDeckContacts={this.props.onDeckContacts}
					onCreateContact={this.props.ImportedCreateContact}
					onUpdateContact={this.props.onUpdateContact}
				/>
				<Modal
					title={this.props?.currentUser?.company?.popupTitle}
					open={
						this.state.isLoginPopupVisible &&
						this.props?.currentUser?.company?.popupTitleContent?.length > 0 &&
						this.props?.currentUser?.company?.popupTitleContent !==
							'<p><br></p>' &&
						this.props?.currentUser?.company?.popupTitleContent !== '<p></p>'
					}
					onCancel={this.handleFirstLoginPopup}
					footer={null}
					centered
					width={520}
				>
					<div
						dangerouslySetInnerHTML={{
							__html: sanitizer(
								this.props?.currentUser?.company?.popupTitleContent
							),
						}}
					/>
					<div
						className="modal-footer-btn"
						style={{ textAlign: 'center', marginTop: 20 }}
					>
						<Button
							type="primary"
							className="btn-min-width"
							onClick={this.handleFirstLoginPopup}
						>
							Got It!
						</Button>
					</div>
				</Modal>
			</main>
		);
	}
}

export default withApollo(DashboardComponent);
